import React, { useEffect, useMemo, useState } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Space, 
  Typography,
  Row,
  Col,
  Card
} from 'antd';
import { 
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { adminPaperApi } from '@/services/api';
import { message } from 'antd';
import AdminLayout from '../../../components/Layouts/AdminLayout/AdminLayout';
import { formatNumber } from '@/utils/number';

const { Title } = Typography;
const { Option } = Select;

interface PaperData {
  id: number;
  name: string;
  price?: number;
  isPublic?: boolean;
  registrant?: string;
  registrationDate?: string;
}

const AdminPaperList: React.FC = () => {
  const navigate = useNavigate();
  const [searchName, setSearchName] = useState<string>('');
  const [searchRegistrant, setSearchRegistrant] = useState<string>('');
  const [searchIsPublic, setSearchIsPublic] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [papers, setPapers] = useState<PaperData[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const loadList = async () => {
    setLoading(true);
    try {
      const isPublic = searchIsPublic === '' ? undefined : searchIsPublic === 'true';
      const res = await adminPaperApi.list({ q: searchName || '', registrant: searchRegistrant || '', isPublic, page: page - 1, size: pageSize });
      const data = res?.data || res;
      const content = data?.content || data?.data?.content || [];
      const totalElements = data?.totalElements || data?.data?.totalElements || 0;
      setPapers(content);
      setTotal(totalElements);
    } catch (e) {
      message.error('시험지 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchName, searchRegistrant, searchIsPublic, page, pageSize]);

  const publicOptions = [
    { value: '', label: '전체' },
    { value: 'true', label: '공개' },
    { value: 'false', label: '비공개' },
  ];

  const handleAddPaper = () => {
    navigate('/admin/paper/detail/add');
  };

  const handleEditPaper = (id: number) => {
    navigate(`/admin/paper/detail/${id}`);
  };

  const handleDeletePaper = (id: string) => {
    // 삭제 로직 구현
    console.log('논문 삭제:', id);
  };

  const filteredPapers = useMemo(() => papers, [papers]);

  const columns = [
    {
      title: '순번',
      dataIndex: 'id',
      key: 'id',
      render: (_: any, __: any, index: number) => formatNumber(total - ((page - 1) * pageSize + index)),
      width: 80
    },
    {
      title: '시험지명',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '가격',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => (price || price === 0) ? `${formatNumber(price)}원` : '-'
    },
    {
      title: '공개여부',
      dataIndex: 'isPublic',
      key: 'isPublic',
      render: (isPublic: boolean) => isPublic ? '공개' : '비공개'
    },
    {
      title: '등록자',
      dataIndex: 'registrant',
      key: 'registrant'
    },
    {
      title: '등록일',
      dataIndex: 'registrationDate',
      key: 'registrationDate',
      render: (date: string) => {
        if (!date) return '-';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hour = String(d.getHours()).padStart(2, '0');
        const minute = String(d.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hour}:${minute}`;
      }
    },
    {
      title: '판매내역',
      key: 'sales',
      render: (_: any, record: any) => (
        <Button
          type="primary"
          size="small"
          onClick={e => {
            e.stopPropagation();
            navigate(`/admin/paper/sales/${record.id}`);
          }}
        >
          판매내역 보기
        </Button>
      ),
      align: 'center' as const,
      width: 120
    }
  ];

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>유료 시험 관리</Title>
        </div>

        {/* 검색 영역 */}
        
          <Row gutter={16} align="bottom" className='mb-4'>
          <Col span={6}>
              <label className="block mb-2 text-sm font-medium">시험지명</label>
              <Input
                placeholder="시험지명을 입력하세요"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                prefix={<SearchOutlined />}
                size='large'
              />
            </Col>
          

            <Col span={4}>
              <label className="block mb-2 text-sm font-medium">등록자</label>
              <Input
                placeholder="등록자를 입력하세요"
                value={searchRegistrant}
                onChange={(e) => setSearchRegistrant(e.target.value)}
                size='large'
              />
            </Col>

            <Col span={4}>
              <label className="block mb-2 text-sm font-medium">공개여부</label>
              <Select
                placeholder="전체"
                value={searchIsPublic}
                onChange={setSearchIsPublic}
                allowClear
                style={{ width: '100%' }}
                size='large'
              >
                {publicOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Col>
          
            <Col span={24} style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddPaper}
                size="large"
              >
                시험 등록
              </Button>
            </Col>
          </Row>

        {/* 테이블 */}
          <Table
            columns={columns}
            dataSource={filteredPapers}
            loading={loading}
            rowKey="id"
            onRow={(record) => ({
              onClick: () => handleEditPaper(record.id),
              style: { cursor: 'pointer' }
            })}
            pagination={{
              total: total,
              current: page,
              pageSize: pageSize,
              showSizeChanger: true,
              onChange: (p, ps) => { setPage(p); setPageSize(ps); },
              showTotal: (t, range) => `${formatNumber(range[0])}-${formatNumber(range[1])} / 총 ${formatNumber(t)}개`
            }}
          />
        
      </div>
    </AdminLayout>
  );
};

export default AdminPaperList; 